import { useCallback, useEffect, useRef, useState } from "react";
import { AgentJobStatus } from "~/graphql/supergraphEnums";
import { useGetDocumentExtractedDataQuery } from "~/graphql/supergraphHooks";

const POLLING_INTERVAL = 2500; // 2.5 seconds
const TOTAL_TIMEOUT = 60000; // 60 seconds

export enum ExtractionStatus {
  Idle = "idle",
  Polling = "polling",
  Succeeded = "succeeded",
  Failed = "failed",
  UnsupportedDocType = "unsupported_doc_type",
}

interface UseDocumentExtractionPollingProps {
  documentId?: number | null;
}

export type DocumentExtractionPollingResult = {
  extractionStatus: ExtractionStatus;
};

export const useDocumentExtractionPolling = ({
  documentId,
}: UseDocumentExtractionPollingProps): DocumentExtractionPollingResult => {
  const [extractionStatus, setExtractionStatus] = useState<ExtractionStatus>(
    ExtractionStatus.Idle,
  );

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const refetchRef = useRef<() => Promise<unknown>>();

  const clearPollingTimers = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const stopPollingWithStatus = useCallback(
    (status: ExtractionStatus) => {
      clearPollingTimers();
      setExtractionStatus(status);
    },
    [clearPollingTimers],
  );

  const { data, loading, error, refetch } = useGetDocumentExtractedDataQuery({
    variables: { docId: documentId ?? 0 },
    skip: !documentId,
    fetchPolicy: "network-only",
  });

  refetchRef.current = refetch;

  const agentJobStatus = data?.document?.agentJobStatus;
  const extractedData = data?.document?.agentExtractedData;
  const hasValidExtractedData = extractedData != null && Object.keys(extractedData).length > 0;

  useEffect(() => {
    if (!documentId || loading || error) {
      return;
    }

    if (agentJobStatus === AgentJobStatus.Succeeded && hasValidExtractedData) {
      stopPollingWithStatus(ExtractionStatus.Succeeded);
      return;
    }

    if (agentJobStatus === AgentJobStatus.Succeeded && !hasValidExtractedData) {
      stopPollingWithStatus(ExtractionStatus.UnsupportedDocType);
      return;
    }

    if (agentJobStatus === AgentJobStatus.Failed) {
      stopPollingWithStatus(ExtractionStatus.Failed);
      return;
    }

    const shouldStartPolling =
      agentJobStatus === AgentJobStatus.Started ||
      agentJobStatus === null ||
      agentJobStatus === undefined;

    if (shouldStartPolling && !intervalRef.current) {
      setExtractionStatus(ExtractionStatus.Polling);

      timeoutRef.current = setTimeout(() => {
        stopPollingWithStatus(ExtractionStatus.Failed);
      }, TOTAL_TIMEOUT);

      intervalRef.current = setInterval(() => {
        void refetchRef.current?.();
      }, POLLING_INTERVAL);
    }

    return clearPollingTimers;
  }, [
    documentId,
    loading,
    error,
    agentJobStatus,
    hasValidExtractedData,
    stopPollingWithStatus,
    clearPollingTimers,
  ]);

  return {
    extractionStatus,
  };
};
